import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { readWindowsLicense } from "@/services/admin-os-image.service";
import {
  OS_IMAGE_COLUMNS,
  useOSImageColumnsStore,
  type OSImageColumnId,
} from "@/stores/osImageColumnsStore";
import type { AdminOSImage } from "@/types/admin";
import { Edit, EyeOff, MoreHorizontal, Power, Trash2 } from "lucide-react";

/**
 * Column widths in px. A full `Record` over the id union so a column added to OS_IMAGE_COLUMNS
 * without a width is a compile error.
 *
 * `image` is the one flexible column. The catalog is small and its columns sum to less than a wide
 * card, and a fixed-layout table spreads that spare width over EVERY column that has one — which is
 * what stretched the Actions column far past its single icon button. Leaving `image` without a
 * <col> width hands all of the spare width to it; its value here is only its minimum.
 */
const COLUMN_WIDTHS: Record<OSImageColumnId, number> = {
  select: 48,
  image: 300,
  category: 112,
  license: 132,
  template: 208,
  specs: 184,
  status: 104,
  usage: 104,
  actions: 80,
};

const FLEXIBLE_COLUMN: OSImageColumnId = "image";

function formatMemory(memoryMb: number): string {
  return memoryMb >= 1024
    ? `${Number((memoryMb / 1024).toFixed(1))} GB`
    : `${memoryMb} MB`;
}

interface OSImageAdminTableProps {
  images: AdminOSImage[];
  isLoading?: boolean;
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  /** Receives every id on the page: selects them all, or clears them when all were selected. */
  onToggleAllSelection: (ids: string[]) => void;
  /** Opens the image. There is no read-only detail page: the edit form is the image's page. */
  onEdit: (image: AdminOSImage) => void;
  onToggleActive: (image: AdminOSImage) => void;
  onDelete: (image: AdminOSImage) => void;
}

export function OSImageAdminTable({
  images,
  isLoading = false,
  selectedIds,
  onToggleSelection,
  onToggleAllSelection,
  onEdit,
  onToggleActive,
  onDelete,
}: OSImageAdminTableProps) {
  // Subscribe to `hidden` (the value that moves) and derive the predicate locally — the store's own
  // `isVisible` is a stable function and would never re-render on a toggle.
  const hiddenColumns = useOSImageColumnsStore((state) => state.hidden);
  const isVisible = (id: OSImageColumnId) => !hiddenColumns.includes(id);
  const visibleColumns = OS_IMAGE_COLUMNS.filter((column) =>
    isVisible(column.id),
  );

  // Derived from the visible columns, never hand-written: the floor below which the flexible
  // column would be squeezed to nothing. On a narrow screen the table stops shrinking here and the
  // scroll box takes over; on a wide one the flexible column absorbs the extra.
  const tableMinWidth = visibleColumns.reduce(
    (sum, column) => sum + COLUMN_WIDTHS[column.id],
    0,
  );

  const pageSelectedCount = images.filter((image) =>
    selectedIds.has(image.id),
  ).length;
  const headerCheckedState =
    images.length > 0 && pageSelectedCount === images.length
      ? true
      : pageSelectedCount > 0
        ? "indeterminate"
        : false;

  if (isLoading) {
    // min-h-0 flex-1 so the card keeps its height while the first page loads.
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    // The scroll lives on this height-bounded box, and the inner wrapper from ui/table is switched
    // off, so the horizontal bar sits at the bottom of the visible table instead of below the last
    // row, and the sticky header resolves against it.
    <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
      <Table
        className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r"
        style={{ minWidth: tableMinWidth }}
      >
        {/* Generated from the column list so a hidden column never leaves its <col> behind. */}
        <colgroup>
          {visibleColumns.map((column) => (
            <col
              key={column.id}
              style={
                column.id === FLEXIBLE_COLUMN
                  ? undefined
                  : { width: COLUMN_WIDTHS[column.id] }
              }
            />
          ))}
        </colgroup>
        <TableHeader className="sticky top-0 z-30 bg-card">
          <TableRow>
            {isVisible("select") && (
              <TableHead className="text-center">
                <Checkbox
                  checked={headerCheckedState}
                  onCheckedChange={() =>
                    onToggleAllSelection(images.map((image) => image.id))
                  }
                  disabled={images.length === 0}
                  aria-label="Select all OS images on this page"
                  className="cursor-pointer align-middle"
                />
              </TableHead>
            )}
            {isVisible("image") && <TableHead>OS Image</TableHead>}
            {isVisible("category") && <TableHead>Category</TableHead>}
            {isVisible("license") && <TableHead>License</TableHead>}
            {isVisible("template") && <TableHead>VM Template</TableHead>}
            {isVisible("specs") && <TableHead>Min. Specs</TableHead>}
            {isVisible("status") && <TableHead>Status</TableHead>}
            {isVisible("usage") && (
              <TableHead className="text-right">Usage</TableHead>
            )}
            {isVisible("actions") && (
              <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {images.map((image) => {
            const subtitle = [image.version, image.osType]
              .filter(Boolean)
              .join(" · ");
            const isInUse = image.usageCount > 0;
            const isWindows = image.category === "Windows";
            const license = readWindowsLicense(image.metadata);

            return (
              <TableRow
                key={image.id}
                data-state={selectedIds.has(image.id) ? "selected" : undefined}
                className="hover:bg-muted/50"
              >
                {isVisible("select") && (
                  <TableCell className="text-center">
                    <Checkbox
                      checked={selectedIds.has(image.id)}
                      onCheckedChange={() => onToggleSelection(image.id)}
                      aria-label={`Select ${image.name}`}
                      className="cursor-pointer align-middle"
                    />
                  </TableCell>
                )}

                {isVisible("image") && (
                  <TableCell>
                    <div className="flex min-w-0 items-center gap-3">
                      {image.iconUrl ? (
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-white p-1.5 dark:bg-white">
                          <img
                            src={image.iconUrl}
                            alt=""
                            className="h-full w-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-medium">
                          {image.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 space-y-0.5">
                        <div className="flex min-w-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(image)}
                            className="cursor-pointer truncate text-left font-medium text-primary hover:underline"
                            title={image.name}
                          >
                            {image.name}
                          </button>
                          {image.isFeatured && (
                            <Badge
                              variant="secondary"
                              className="shrink-0 bg-warn-soft px-1.5 py-0 text-[11px] text-warn-ink"
                            >
                              Featured
                            </Badge>
                          )}
                        </div>
                        {subtitle && (
                          <p className="truncate text-xs text-muted-foreground">
                            {subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}

                {isVisible("category") && (
                  <TableCell>
                    <Badge variant="outline">{image.category}</Badge>
                  </TableCell>
                )}

                {/* License activation is a Windows setting; other categories have nothing to say. */}
                {isVisible("license") && (
                  <TableCell>
                    {!isWindows ? (
                      <span className="text-muted-foreground">—</span>
                    ) : license.activationSupported ? (
                      <Badge
                        variant="secondary"
                        className="bg-ok-soft text-ok-ink"
                      >
                        Supported
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="bg-warn-soft text-warn-ink"
                        title={license.warningMessage || undefined}
                      >
                        Not supported
                      </Badge>
                    )}
                  </TableCell>
                )}

                {isVisible("template") && (
                  <TableCell>
                    <code
                      className="block truncate font-mono text-xs text-muted-foreground"
                      title={image.templateName}
                    >
                      {image.templateName || "—"}
                    </code>
                  </TableCell>
                )}

                {isVisible("specs") && (
                  <TableCell className="whitespace-nowrap text-sm">
                    {image.minimumSpecs.cpu} vCPU ·{" "}
                    {formatMemory(image.minimumSpecs.memory)} ·{" "}
                    {image.minimumSpecs.disk} GB
                  </TableCell>
                )}

                {isVisible("status") && (
                  <TableCell>
                    <Badge variant={image.isActive ? "default" : "secondary"}>
                      {image.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                )}

                {isVisible("usage") && (
                  <TableCell className="text-right">
                    <div className="text-sm font-medium tabular-nums">
                      {image.usageCount} VMs
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {image.activeVMCount} active
                    </div>
                  </TableCell>
                )}

                {isVisible("actions") && (
                  <TableCell className="sticky right-0 z-10 bg-card">
                    <div className="flex items-center justify-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Actions for ${image.name}`}
                            className="h-8 w-8 p-0"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEdit(image)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onToggleActive(image)}>
                            {image.isActive ? (
                              <EyeOff className="mr-2 h-4 w-4" />
                            ) : (
                              <Power className="mr-2 h-4 w-4" />
                            )}
                            {image.isActive ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive-ink"
                            onClick={() => onDelete(image)}
                            disabled={isInUse}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {isInUse ? "Delete (in use)" : "Delete"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
